// stripe.component.ts
import { Component, ElementRef, OnInit, ViewChild, AfterViewInit, OnDestroy } from '@angular/core';
import { SharedDataService } from 'src/app/services/shared-data.service';
import { StripeService } from 'src/app/services/stripe.service';
import { LoadingService } from 'src/app/services/loading.service';
import { ErrorHandlerService } from 'src/app/services/error-handler.service';
import { AuthService } from 'src/app/services/auth.service';
import { StripeTransactionInfo } from '../../models/stripe.model';
import { Config, Plan, PriceRange } from '../../models/config.model';
import { environment } from 'src/environments/environment';
import { NavigationService } from 'src/app/services/navigation.service';

declare var Stripe: any;

@Component({
  selector: 'app-stripe',
  template: `
  <div class="stripe-container">
    <div id="stripe-element" #stripeElement>
      <!-- Stripe will insert the payment form here -->
    </div>
    <div *ngIf="checkoutInstance && !isDirectEntry" class="stripe-back-button-container">
      <button (click)="onBack()" class="stripe-back-button">BACK</button>
    </div>
  </div>
  `,
  styleUrls: ['./stripe.component.scss']
})
export class StripeComponent implements OnInit, AfterViewInit, OnDestroy {
  @ViewChild('stripeElement') stripeElementRef: ElementRef;
  config: Config;
  stripe: any;
  parentKey: string = 'SKILLCHECK2';
  customerId: string = 'QATEST';
  uid: string;
  description: string = 'New Customer Sign Up';
  taxExempt: boolean = false;
  selected: Plan;
  testUsageRangeIndex: number;
  integrationName: string;
  checkoutInstance: any;
  formInfo: any;
  clientSecret: string;

  constructor(
    private authService: AuthService,
    private sharedDataService: SharedDataService,
    private stripeService: StripeService,
    private loadingService: LoadingService,
    private errorHandlerService: ErrorHandlerService,
    private navigationService: NavigationService
  ) {}

  ngOnInit(): void {
    this.sharedDataService.getConfig().subscribe(config => this.config = config);
    this.formInfo = this.sharedDataService.getFormInfo();

    if (!this.authService.isExistingCustomerMode()) {
      this.sharedDataService.getPlan().subscribe(plan => this.selected = plan);
      this.sharedDataService.getRangeIndex().subscribe(index => this.testUsageRangeIndex = index);
      this.sharedDataService.getSelectedIntegration().subscribe(integration => {
        if (integration) {
          this.integrationName = integration.name;
        }
      });
    }
  }

  onBack(): void {
    // Navigate back to checkout screen by updating navigation state.
    this.navigationService.navigateBack();
  }

  ngAfterViewInit(): void {
    this.startStripeSession();
  }

  ngOnDestroy(): void {
    if (this.checkoutInstance) {
      this.checkoutInstance.unmount();
      this.checkoutInstance.destroy();
    }
  }

  get isDirectEntry(): boolean {
    return this.sharedDataService.isDirectEntry();
  }

  get selectedPriceRange(): PriceRange | null {
    if (this.selected && this.selected.priceRanges && this.selected.priceRanges.length > this.testUsageRangeIndex) {
      return this.selected.priceRanges[this.testUsageRangeIndex];
    }
    return null;
  }

  get selectedPrice(): number | null {
    if (this.selectedPriceRange) {
      return this.selectedPriceRange.price;
    }
    return null;
  }

  get selectedRange(): string | null {
    if (this.selectedPriceRange) {
      return `${this.selectedPriceRange.min}-${this.selectedPriceRange.max}`;
    }
    return '';
  }

  private loadStripe(): void {
    let key;
    const env = this.sharedDataService.getEnv();
    if (env === 'gamma') {
      key = environment.stripePublishableKeyGamma;
    } else if (env === 'webtest') {
      key = environment.stripePublishableKeyProd;
    } else {
      key = environment.stripePublishableKeyDev;
    }
    if (!Stripe) {
      const s = window.document.createElement('script');
      s.id = 'stripe-script';
      s.type = 'text/javascript';
      s.src = 'https://js.stripe.com/v3/';
      s.onload = () => {
        this.stripe = Stripe(key);
        this.initializeStripeForm();
      };
      window.document.body.appendChild(s);
    } else {
      try {
        this.stripe = Stripe(key);
      } catch (e) {
        this.handleError(e);
        this.navigationService.navigateBack();
      }
      this.initializeStripeForm();
    }
  }

  private async initializeStripeForm(): Promise<void> {
    if (!this.stripe) {
      console.error('Stripe has not been initialized. Please check your Stripe JS inclusion.');
      return;
    }
    const fetchClientSecret = async () => Promise.resolve(this.clientSecret);
    try {
      this.checkoutInstance = await this.stripe.initEmbeddedCheckout({ fetchClientSecret });
      this.checkoutInstance.mount(this.stripeElementRef.nativeElement);
    } catch (error) {
      console.error('Failed to initialize Stripe Checkout:', error);
    }
    this.loadingService.hide();
  }

  redirectToStripe(stripeURL: string): void {
    window.location.href = stripeURL;
  }

  startStripeSession(): void {
    this.loadingService.show();
    const purchaseDetails: StripeTransactionInfo = this.preparePurchaseDetails();
    this.stripeService.stripeCheckout(purchaseDetails).subscribe({
      next: (response: any) => this.handleStripeResponse(response),
      error: (error: any) => this.handleError(error)
    });
  }

  private handleStripeResponse(response: any): void {
    const stripeURL = response?.result?.url;
    const clientSecret = response?.result?.clientSecret;
    if (stripeURL) {
      this.redirectToStripe(stripeURL);
    } else if (clientSecret) {
      this.clientSecret = clientSecret;
      this.loadStripe();
    } else {
      this.errorHandlerService.handleError(new Error('Failed to initiate the payment process. Please try again.'), 'Payment Initialization Failed');
      this.navigationService.navigateBack();
      this.loadingService.hide();
    }
  }

  private handleError(error: any): void {
    this.errorHandlerService.handleError(error, 'Failed to initiate the payment process. Please try again.');
    this.loadingService.hide();
  }

  private preparePurchaseDetails(): StripeTransactionInfo {
    let userAgent = '{';
    userAgent += 'Browser CodeName: ' + navigator.appCodeName + ',';
    userAgent += 'Browser Name: ' + navigator.appName + ',';
    userAgent += 'Browser Version: ' + navigator.appVersion + ',';
    userAgent += 'Cookies Enabled: ' + navigator.cookieEnabled + ',';
    userAgent += 'Browser Language: ' + navigator.language + ',';
    userAgent += 'Browser Online: ' + navigator.onLine + ',';
    userAgent += 'Platform: ' + navigator.platform + ',';
    userAgent += 'User-agent header: ' + navigator.userAgent;
    userAgent += '}';

    this.uid = this.stripeService.createUID();

    let purchaseDetails: StripeTransactionInfo;
    const vals = this.formInfo;
    purchaseDetails = {
      origin: 'eComm.Stripe',
      uid: this.uid,
      customerID: this.customerId,
      planName: this.selected?.title || '',
      estimatedUsage: this.selectedRange || '',
      integration: this.integrationName || '',
      description: this.description,
      isTaxExempt: this.taxExempt,
      contactName: vals.cardholderName,
      contactCompanyName: vals.companyName || '',
      contactAddress1: vals.address1,
      contactAddress2: vals.address2,
      contactCity: vals.city,
      contactState: vals.state || vals.mexicoProvince || vals.canadaProvince || null,
      contactZip: vals.zip,
      contactCountry: vals.country,
      contactEmail: vals.email,
      contactPhone: vals.phone || '',
      contactCompanyURL: vals.cardholderURL || '',
      paymentMethod: 'credit',
      transactionType: 'P',
      billMethodOrderType: 'MeteredUsageBill',
      skUserId: this.customerId,
      isNewCustomer: 'true',
      userAgent: userAgent,
      reference: vals.reference,
      purchaseType: this.selected?.title.toLocaleLowerCase() || '',
      billingPeriod: (this.sharedDataService.getBillingTerm() || 'annual').toUpperCase(),
      env: this.sharedDataService.getEnv()
    };

    if (this.authService.isExistingCustomerMode()) {
      const creditCardInfoStripe = this.sharedDataService.getCreditCardInfoStripe();
      purchaseDetails = { ...purchaseDetails, ...creditCardInfoStripe };
    }
    return purchaseDetails;
  }
}
